import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AdjustmentsHorizontalIcon,
  ArchiveBoxIcon,
  BanknotesIcon,
  CalculatorIcon,
  ClipboardDocumentListIcon,
  CubeIcon,
  MagnifyingGlassIcon,
  PlusCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import axios from '../services/api';
import toast from 'react-hot-toast';
import {
  INVENTORY_DOMAIN_SEQUENCE,
  sortInventoryItemsByMasterSequence,
} from '../utils/inventoryMasterSequence';
import useFinanceLockStatus from '../hooks/useFinanceLockStatus';
import useAuthStore from '../store/useAuthStore';
import { normalizeRole, ROLE_KEYS } from '../utils/rolePermissions';
import FinanceLockGate from '../components/FinanceLockGate';

const RUPEE_SYMBOL = '\u20B9';

const domains = INVENTORY_DOMAIN_SEQUENCE;

const shellClasses =
  'rounded-[28px] border border-slate-200 bg-white shadow-[0_12px_28px_rgba(15,23,42,0.05)]';
const railClasses =
  'mobile-table-scroll rounded-[24px] border border-slate-200 bg-slate-50/80 p-2 shadow-[0_10px_24px_rgba(15,23,42,0.05)]';
const railButtonBase =
  'rounded-full px-4 py-2.5 text-sm font-semibold whitespace-nowrap transition';
const railButtonActive = 'bg-blue-600 text-white shadow-[0_8px_20px_rgba(37,99,235,0.22)]';
const railButtonInactive =
  'border border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-900';
const tableHeaderClasses =
  'px-4 py-3.5 text-center text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500';
const tableCellClasses = 'px-4 py-4 whitespace-nowrap text-center text-sm text-slate-600';
const inputShellClasses =
  'inline-flex items-center rounded-full border px-3 py-1.5 shadow-[0_2px_8px_rgba(15,23,42,0.04)]';
const restockPillBaseClasses =
  'inline-flex min-w-[78px] items-center justify-center rounded-full border px-3 py-1.5 text-sm font-semibold';
const modalOverlayClasses =
  'fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/40 px-2 py-4 backdrop-blur-sm sm:items-center sm:px-4 sm:py-6';
const modalPanelClasses =
  'max-h-[calc(100dvh-1rem)] w-full max-w-3xl overflow-y-auto rounded-[24px] border border-slate-200 bg-white shadow-[0_24px_70px_rgba(15,23,42,0.24)] sm:rounded-[28px]';
const formLabelClasses =
  'block text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500';
const formInputClasses =
  'mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-800 outline-none transition focus:border-blue-300 focus:bg-white focus:ring-4 focus:ring-blue-50 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500';

const parseNumericValue = (value) => {
  const numericValue = Number(value);

  return Number.isFinite(numericValue) ? numericValue : 0;
};

const formatCurrencyValue = (value) => parseNumericValue(value).toFixed(2);

export const calculatePackQuantity = ({ looseQty = '', fullPacks = '', unitsPerPack = '' } = {}) => {
  const rawValues = [looseQty, fullPacks, unitsPerPack];
  const hasEntry = rawValues.some((value) => value !== '' && value !== null && value !== undefined);

  if (!hasEntry) {
    return { error: 'Enter Loose Qty or Full Packs to calculate the total.' };
  }

  const numericValues = rawValues.map((value) => {
    if (value === '' || value === null || value === undefined) return 0;
    return Number(value);
  });

  if (numericValues.some((value) => !Number.isFinite(value))) {
    return { error: 'Enter valid numeric quantities.' };
  }

  if (numericValues.some((value) => value < 0)) {
    return { error: 'Quantities cannot be negative.' };
  }

  const [loose, packs, units] = numericValues;
  if (packs > 0 && units <= 0) {
    return { error: 'Units per Pack must be greater than zero when Full Packs are entered.' };
  }

  // Inventory quantities are already normalized to two decimals by the existing API.
  const total = Math.round((loose + packs * units + Number.EPSILON) * 100) / 100;
  return { total };
};

const formatCurrencyDisplay = (value) => {
  if (value === null || value === undefined || value === '') {
    return 'Locked';
  }

  return `${RUPEE_SYMBOL}${formatCurrencyValue(value)}`;
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return new Intl.DateTimeFormat('en-IN', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

const formatMovementType = (value) =>
  String(value || '-')
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const getFriendlyInventoryError = (error) => {
  const status = error?.response?.status;
  const backendCode = error?.response?.data?.code;
  const backendMessage = error?.response?.data?.error || error?.message || '';

  if (status === 423 || backendCode === 'FINANCE_LOCK_REQUIRED') {
    return 'Finance Lock is enabled. Unlock finance access before saving purchase values.';
  }

  if (status === 403) {
    return 'You do not have permission for this inventory action.';
  }

  if (status === 404) {
    return 'This stock item was not found for your clinic.';
  }

  return backendMessage || 'Inventory request failed.';
};

const getInventoryAmount = (item) =>
  parseNumericValue(item?.currentStockQty) * parseNumericValue(item?.ratePerUnit);

const getBalanceTonePresentation = (percentageValue) => {
  const percentage = parseNumericValue(percentageValue);

  if (percentage < 20) {
    return {
      tone: 'critical',
      className: `${restockPillBaseClasses} border-rose-200 bg-rose-50 text-rose-700`,
    };
  }

  if (percentage <= 40) {
    return {
      tone: 'warning',
      className: `${restockPillBaseClasses} border-amber-200 bg-amber-50 text-amber-700`,
    };
  }

  return {
    tone: 'sufficient',
    className: `${restockPillBaseClasses} border-emerald-200 bg-emerald-50 text-emerald-700`,
  };
};

function MoneyInput({ value, onChange, onBlur, onKeyDown, ariaLabel, starter = false }) {
  return (
    <div className={`${inputShellClasses} ${starter ? 'border-yellow-400 bg-yellow-100' : 'border-slate-200 bg-slate-50'}`}>
      <span className="text-sm font-semibold text-slate-400">{RUPEE_SYMBOL}</span>
      <input
        type="number"
        step="0.01"
        min="0"
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        aria-label={ariaLabel}
        className="w-20 border-0 bg-transparent px-2 text-center text-sm font-semibold text-slate-700 outline-none focus:ring-0"
      />
    </div>
  );
}

export function QuantityInput({
  value,
  onChange,
  onBlur,
  onKeyDown,
  onCalculatedValue,
  ariaLabel,
  starter = false,
}) {
  const [calculatorOpen, setCalculatorOpen] = useState(false);
  const [packDraft, setPackDraft] = useState({ looseQty: '', fullPacks: '', unitsPerPack: '' });
  const [calculatorPosition, setCalculatorPosition] = useState({ left: 12, top: 12 });
  const calculatorButtonRef = useRef(null);
  const calculation = calculatePackQuantity(packDraft);

  useEffect(() => {
    if (!calculatorOpen) return undefined;

    const positionCalculator = () => {
      const button = calculatorButtonRef.current;
      if (!button) return;

      const rect = button.getBoundingClientRect();
      const viewportPadding = 12;
      const popoverWidth = Math.min(320, window.innerWidth - viewportPadding * 2);
      const left = Math.min(
        Math.max(viewportPadding, rect.left + rect.width / 2 - popoverWidth / 2),
        window.innerWidth - popoverWidth - viewportPadding
      );
      const opensAbove = window.innerHeight - rect.bottom < 390 && rect.top > window.innerHeight - rect.bottom;

      setCalculatorPosition(
        opensAbove
          ? { left, bottom: window.innerHeight - rect.top + 8 }
          : { left, top: rect.bottom + 8 }
      );
    };

    positionCalculator();
    window.addEventListener('resize', positionCalculator);
    window.addEventListener('scroll', positionCalculator, true);

    return () => {
      window.removeEventListener('resize', positionCalculator);
      window.removeEventListener('scroll', positionCalculator, true);
    };
  }, [calculatorOpen]);

  const updatePackDraft = (field, nextValue) => {
    setPackDraft((current) => ({ ...current, [field]: nextValue }));
  };

  const useCalculatedValue = () => {
    if (calculation.error) return;
    onCalculatedValue(String(calculation.total));
    setCalculatorOpen(false);
  };

  return (
    <div className="relative inline-flex items-center gap-1.5">
      <div className={`${inputShellClasses} ${starter ? 'border-yellow-400 bg-yellow-100' : 'border-slate-200 bg-slate-50'}`}>
        <input
          type="number"
          step="0.01"
          min="0"
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          aria-label={ariaLabel}
          className="w-20 border-0 bg-transparent text-center text-sm font-semibold text-slate-700 outline-none focus:ring-0"
        />
      </div>
      <button
        ref={calculatorButtonRef}
        type="button"
        onClick={() => setCalculatorOpen((open) => !open)}
        aria-expanded={calculatorOpen}
        aria-label={`Calculate ${ariaLabel} from packs`}
        title="Calculate from packs"
        className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-blue-200 bg-blue-50 text-blue-700 transition hover:bg-blue-100 focus:outline-none focus:ring-4 focus:ring-blue-100"
      >
        <CalculatorIcon className="h-4 w-4" />
      </button>
      {calculatorOpen ? createPortal(
        <div
          role="dialog"
          aria-label={`Pack calculator for ${ariaLabel}`}
          style={calculatorPosition}
          className="fixed z-[70] max-h-[calc(100dvh-1.5rem)] w-[calc(100vw-1.5rem)] max-w-xs overflow-y-auto rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-[0_20px_55px_rgba(15,23,42,0.22)]"
        >
          <div className="flex items-center justify-between gap-3 border-b border-slate-100 pb-3">
            <p className="text-sm font-semibold text-slate-900">Calculate from packs</p>
            <button
              type="button"
              onClick={() => setCalculatorOpen(false)}
              aria-label="Close pack calculator"
              className="rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
          <div className="mt-4 space-y-3">
            {[
              ['looseQty', 'Loose Qty'],
              ['fullPacks', 'Full Packs'],
              ['unitsPerPack', 'Units / Pack'],
            ].map(([field, label]) => (
              <label key={field} className="block">
                <span className="block text-xs font-semibold text-slate-600">{label}</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={packDraft[field]}
                  onChange={(event) => updatePackDraft(field, event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      useCalculatedValue();
                    }
                    if (event.key === 'Escape') setCalculatorOpen(false);
                  }}
                  aria-label={label}
                  className="mt-1.5 block w-full min-w-0 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm font-semibold text-slate-800 outline-none transition focus:border-blue-300 focus:bg-white focus:ring-4 focus:ring-blue-50"
                />
              </label>
            ))}
          </div>
          <div className="mt-4 rounded-xl border border-blue-100 bg-blue-50 px-3.5 py-3">
            <p className="text-sm font-semibold text-blue-900" aria-live="polite">
              Total Qty: {calculation.error ? '—' : calculation.total}
            </p>
          </div>
          <button
            type="button"
            disabled={Boolean(calculation.error)}
            onClick={useCalculatedValue}
            className="mt-3 w-full rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500"
          >
            Use calculated Qty
          </button>
        </div>,
        document.body
      ) : null}
    </div>
  );
}

function AmountInput({ value, quantity, onCommit, ariaLabel, starter = false }) {
  const formattedValue = formatCurrencyValue(value);
  const [draftValue, setDraftValue] = useState(formattedValue);

  useEffect(() => {
    setDraftValue(formattedValue);
  }, [formattedValue]);

  const commitValue = () => {
    const normalizedDraft = draftValue === '' ? '0' : draftValue;
    const nextAmount = parseNumericValue(normalizedDraft);
    const currentAmount = parseNumericValue(value);

    if (nextAmount === currentAmount) {
      setDraftValue(formattedValue);
      return;
    }

    const numericQuantity = parseNumericValue(quantity);

    if (numericQuantity <= 0) {
      setDraftValue(formattedValue);
      toast.error('Enter Current Stock Qty before updating Amount');
      return;
    }

    const nextRatePerUnit = (nextAmount / numericQuantity).toFixed(8);
    onCommit(nextRatePerUnit);
    setDraftValue(nextAmount.toFixed(2));
  };

  return (
    <div className={`${inputShellClasses} ${starter ? 'border-yellow-400 bg-yellow-100' : 'border-slate-200 bg-slate-50'}`}>
      <span className="text-sm font-semibold text-slate-400">{RUPEE_SYMBOL}</span>
      <input
        type="number"
        step="0.01"
        min="0"
        value={draftValue}
        onChange={(e) => setDraftValue(e.target.value)}
        onBlur={commitValue}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.currentTarget.blur();
          }
        }}
        aria-label={ariaLabel}
        className="w-24 border-0 bg-transparent px-2 text-center text-sm font-semibold text-slate-700 outline-none focus:ring-0"
      />
    </div>
  );
}

function ModalShell({ children, onClose, title }) {
  return (
    <div className={modalOverlayClasses} role="dialog" aria-modal="true" aria-label={title}>
      <div className={modalPanelClasses}>
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-slate-200 bg-white px-4 py-4 sm:px-6 sm:py-5">
          <div>
            <h2 className="text-lg font-semibold tracking-tight text-slate-900">{title}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition hover:border-rose-200 hover:text-rose-600"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ReadOnlyField({ label, value }) {
  return (
    <div>
      <span className={formLabelClasses}>{label}</span>
      <div className="mt-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-800">
        {value || '-'}
      </div>
    </div>
  );
}

function RestockModal({
  draft,
  financeStatus,
  item,
  onChange,
  onClose,
  onSubmit,
  saving,
}) {
  if (!item) return null;

  const stockAfterPreview =
    parseNumericValue(item.currentStockQty) + parseNumericValue(draft.newAddedQty);
  const amountPreview =
    parseNumericValue(draft.newAddedQty) * parseNumericValue(draft.ratePerUnit);
  const canShowMoneyInputs =
    !financeStatus.loading && (!financeStatus.locked || financeStatus.restricted);

  if (financeStatus.locked && !financeStatus.restricted) {
    return (
      <ModalShell title="Restock Consumable" onClose={onClose}>
        <div className="p-6">
          <FinanceLockGate
            compact
            description="Finance Lock protects inventory purchase rates and restock values."
            onAccessGranted={financeStatus.refresh}
            onCancel={onClose}
            title="Finance Lock enabled. Enter PIN to restock."
          >
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700">
              Finance access is unlocked. You can continue restocking this item.
            </div>
          </FinanceLockGate>
        </div>
      </ModalShell>
    );
  }

  return (
    <ModalShell title="Restock Consumable" onClose={onClose}>
      <form onSubmit={onSubmit} className="space-y-5 p-6">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <ReadOnlyField label="Consumable" value={item.name} />
          <ReadOnlyField label="Type / Variant" value={item.typeVariant} />
          <ReadOnlyField label="Current Balance Qty" value={formatCurrencyValue(item.currentStockQty).replace(`${RUPEE_SYMBOL}`, '')} />
          <ReadOnlyField label="Unit" value={item.unit} />
        </div>

        {financeStatus.loading ? (
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-600">
            Checking Finance Lock before showing purchase values...
          </div>
        ) : null}

        {financeStatus.locked && !financeStatus.restricted ? (
          <div className="rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm font-semibold text-blue-700">
            Finance Lock is enabled. Unlock finance access before entering purchase rate and supplier values.
          </div>
        ) : null}

        {financeStatus.restricted ? (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-700">
            Finance status is restricted for this user. The backend will allow this save only if your role is permitted for inventory purchase values.
          </div>
        ) : null}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <label>
            <span className={formLabelClasses}>New Added Qty</span>
            <input
              type="number"
              step="0.01"
              min="0.01"
              required
              value={draft.newAddedQty}
              onChange={(event) => onChange('newAddedQty', event.target.value)}
              className={formInputClasses}
            />
          </label>

          <label>
            <span className={formLabelClasses}>Rate Per Unit</span>
            <div className="relative">
              <span className="pointer-events-none absolute left-4 top-[22px] text-sm font-semibold text-slate-400">
                {RUPEE_SYMBOL}
              </span>
              <input
                type="number"
                step="0.01"
                min="0"
                required
                disabled={!canShowMoneyInputs && financeStatus.locked}
                value={draft.ratePerUnit}
                onChange={(event) => onChange('ratePerUnit', event.target.value)}
                className={`${formInputClasses} pl-8`}
              />
            </div>
          </label>

          <label>
            <span className={formLabelClasses}>Supplier / Vendor</span>
            <input
              type="text"
              value={draft.supplierName}
              onChange={(event) => onChange('supplierName', event.target.value)}
              className={formInputClasses}
              placeholder="Optional"
            />
          </label>

          <label>
            <span className={formLabelClasses}>Purchase Date</span>
            <input
              type="date"
              value={draft.purchaseDate}
              onChange={(event) => onChange('purchaseDate', event.target.value)}
              className={formInputClasses}
            />
          </label>
        </div>

        <label>
          <span className={formLabelClasses}>Notes</span>
          <textarea
            value={draft.notes}
            onChange={(event) => onChange('notes', event.target.value)}
            rows={3}
            className={`${formInputClasses} resize-none`}
            placeholder="Optional"
          />
        </label>

        <div className="grid grid-cols-1 gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm md:grid-cols-3">
          <div>
            <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
              Balance After
            </span>
            <span className="mt-1 block font-semibold text-slate-900">
              {stockAfterPreview.toFixed(2)}
            </span>
          </div>
          <div>
            <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
              Restock Value
            </span>
            <span className="mt-1 block font-semibold text-slate-900">
              {financeStatus.locked && !financeStatus.restricted
                ? 'Locked'
                : financeStatus.loading
                  ? 'Checking...'
                  : formatCurrencyDisplay(amountPreview)}
            </span>
          </div>
          <div>
            <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
              Ledger Movement
            </span>
            <span className="mt-1 block font-semibold text-slate-900">Restock Addition</span>
          </div>
        </div>

        <div className="mobile-stack-actions flex flex-wrap justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={
              saving || financeStatus.loading || (financeStatus.locked && !financeStatus.restricted)
            }
            className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <PlusCircleIcon className="h-4 w-4" />
            {saving ? 'Saving...' : 'Save Restock'}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function AdjustmentModal({ draft, item, onChange, onClose, onSubmit, saving }) {
  if (!item) return null;

  const nextQty = parseNumericValue(item.currentStockQty) + parseNumericValue(draft.adjustmentQty);

  return (
    <ModalShell title="Manual Stock Adjustment" onClose={onClose}>
      <form onSubmit={onSubmit} className="space-y-5 p-6">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <ReadOnlyField label="Consumable" value={item.name} />
          <ReadOnlyField label="Type / Variant" value={item.typeVariant} />
          <ReadOnlyField label="Current Balance Qty" value={formatCurrencyValue(item.currentStockQty)} />
          <ReadOnlyField label="Unit" value={item.unit} />
        </div>

        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-800">
          Manual adjustment is for correction only. Use Restock for purchased stock.
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <label>
            <span className={formLabelClasses}>Adjustment Qty</span>
            <input
              type="number"
              step="0.01"
              required
              value={draft.adjustmentQty}
              onChange={(event) => onChange('adjustmentQty', event.target.value)}
              className={formInputClasses}
              placeholder="-2 or 5"
            />
          </label>

          <ReadOnlyField label="Balance After" value={nextQty.toFixed(2)} />
        </div>

        <label>
          <span className={formLabelClasses}>Reason</span>
          <input
            type="text"
            required
            value={draft.reason}
            onChange={(event) => onChange('reason', event.target.value)}
            className={formInputClasses}
            placeholder="Reason is required"
          />
        </label>

        <label>
          <span className={formLabelClasses}>Notes</span>
          <textarea
            value={draft.notes}
            onChange={(event) => onChange('notes', event.target.value)}
            rows={3}
            className={`${formInputClasses} resize-none`}
            placeholder="Optional"
          />
        </label>

        <div className="mobile-stack-actions flex flex-wrap justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <AdjustmentsHorizontalIcon className="h-4 w-4" />
            {saving ? 'Saving...' : 'Save Adjustment'}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function LedgerModal({ item, ledger, loading, onClose }) {
  if (!item) return null;

  return (
    <ModalShell title="Stock Movement History" onClose={onClose}>
      <div className="space-y-5 p-6">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <ReadOnlyField label="Consumable" value={item.name} />
          <ReadOnlyField label="Type / Variant" value={item.typeVariant} />
          <ReadOnlyField label="Current Balance Qty" value={formatCurrencyValue(item.currentStockQty)} />
        </div>

        <div className="mobile-table-scroll rounded-2xl border border-slate-200">
          <table className="mobile-card-table min-w-[980px] divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className={tableHeaderClasses}>Date</th>
                <th className={tableHeaderClasses}>Movement Type</th>
                <th className={tableHeaderClasses}>Qty Before</th>
                <th className={tableHeaderClasses}>Qty Change</th>
                <th className={tableHeaderClasses}>Qty After</th>
                <th className={tableHeaderClasses}>Rate / Unit</th>
                <th className={tableHeaderClasses}>Total Value</th>
                <th className={tableHeaderClasses}>Reason / Reference</th>
                <th className={tableHeaderClasses}>Created By</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {loading ? (
                <tr>
                  <td colSpan="9" data-full-row="true" className="px-6 py-8 text-center text-sm text-slate-500">
                    Loading stock ledger...
                  </td>
                </tr>
              ) : ledger.length > 0 ? (
                ledger.map((movement) => (
                  <tr key={movement.id}>
                    <td data-label="Date" className={tableCellClasses}>{formatDateTime(movement.createdAt)}</td>
                    <td data-label="Movement" className={tableCellClasses}>{formatMovementType(movement.movementType)}</td>
                    <td data-label="Qty Before" className={tableCellClasses}>{formatCurrencyValue(movement.quantityBefore)}</td>
                    <td data-label="Qty Change" className={tableCellClasses}>{formatCurrencyValue(movement.quantityChange)}</td>
                    <td data-label="Qty After" className={tableCellClasses}>{formatCurrencyValue(movement.quantityAfter)}</td>
                    <td data-label="Rate / Unit" className={tableCellClasses}>{formatCurrencyDisplay(movement.ratePerUnitSnapshot)}</td>
                    <td data-label="Total Value" className={tableCellClasses}>{formatCurrencyDisplay(movement.totalValueSnapshot)}</td>
                    <td data-label="Reason" className={`${tableCellClasses} max-w-[240px] whitespace-normal text-left`}>
                      <span className="font-semibold text-slate-700">
                        {movement.reason || '-'}
                      </span>
                      {movement.referenceType || movement.referenceId ? (
                        <span className="mt-1 block text-xs text-slate-400">
                          {[movement.referenceType, movement.referenceId].filter(Boolean).join(' #')}
                        </span>
                      ) : null}
                    </td>
                    <td data-label="Created By" className={tableCellClasses}>{movement.createdBy || '-'}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" data-full-row="true" className="px-6 py-8 text-center text-sm text-slate-500">
                    No stock movement history found for this item yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </ModalShell>
  );
}

function ClinicInventoryModal({ open, item, options, saving, onClose, onSave }) {
  const [draft, setDraft] = useState(null);
  const [search, setSearch] = useState('');
  useEffect(() => {
    if (!open) return;
    setDraft({
      name: item?.name || '', domain: item?.domain || options.domains?.[0] || '',
      typeVariant: item?.typeVariant || '', unit: item?.unit || options.units?.[0] || 'Piece',
      mappings: (item?.mappings || []).map((row) => ({ ...row, key: `${row.sourceType}:${row.treatmentId}` })),
    });
    setSearch('');
  }, [open, item, options]);
  if (!open || !draft) return null;
  const selected = new Map(draft.mappings.map((row) => [row.key, row]));
  const visibleTreatments = (options.treatments || []).filter((row) =>
    `${row.name} ${row.domain}`.toLowerCase().includes(search.toLowerCase())
  );
  const toggle = (treatment) => {
    const key = `${treatment.sourceType}:${treatment.treatmentId}`;
    setDraft((current) => ({ ...current, mappings: selected.has(key)
      ? current.mappings.filter((row) => row.key !== key)
      : [...current.mappings, { ...treatment, key, quantityUsed: 1 }],
    }));
  };
  return (
    <ModalShell title={item ? 'Edit clinic inventory' : 'Add clinic inventory'} onClose={onClose}>
      <form className="space-y-5 p-5 sm:p-6" onSubmit={(event) => { event.preventDefault(); onSave(draft); }}>
        <div className="grid gap-4 sm:grid-cols-2">
          <label><span className={formLabelClasses}>Inventory Name</span><input className={formInputClasses} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} required /></label>
          <label><span className={formLabelClasses}>Category</span><select className={formInputClasses} value={draft.domain} onChange={(e) => setDraft({ ...draft, domain: e.target.value })}>{options.domains.map((value) => <option key={value}>{value}</option>)}</select></label>
          <label><span className={formLabelClasses}>Type / Variant</span><input className={formInputClasses} value={draft.typeVariant} placeholder="Standard" onChange={(e) => setDraft({ ...draft, typeVariant: e.target.value })} /></label>
          <label><span className={formLabelClasses}>Stock Unit</span><select className={formInputClasses} value={draft.unit} onChange={(e) => setDraft({ ...draft, unit: e.target.value })}>{options.units.map((value) => <option key={value}>{value}</option>)}</select></label>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <div className="flex items-center justify-between gap-3"><div><h4 className="text-sm font-semibold text-slate-900">Used In Treatments</h4><p className="text-xs text-slate-500">Optional · {draft.mappings.length} selected</p></div></div>
          <input type="search" className={formInputClasses} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search treatments" />
          <div className="mt-3 max-h-48 space-y-2 overflow-y-auto pr-1">
            {visibleTreatments.map((treatment) => { const key = `${treatment.sourceType}:${treatment.treatmentId}`; const mapping = selected.get(key); return (
              <div key={key} className="flex flex-col gap-2 rounded-xl border border-slate-200 bg-white p-3 sm:flex-row sm:items-center">
                <label className="flex min-w-0 flex-1 items-center gap-2"><input type="checkbox" checked={Boolean(mapping)} onChange={() => toggle(treatment)} /><span className="min-w-0"><span className="block truncate text-sm font-semibold text-slate-800">{treatment.name}</span><span className="block truncate text-xs text-slate-500">{treatment.domain}{treatment.isCustom ? ' · Clinic Added' : ''}</span></span></label>
                {mapping ? <label className="flex items-center gap-2 text-xs font-semibold text-slate-500">Qty Used<input type="number" min="0" step="0.01" className="w-24 rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-800" value={mapping.quantityUsed} onChange={(e) => setDraft((current) => ({ ...current, mappings: current.mappings.map((row) => row.key === key ? { ...row, quantityUsed: e.target.value } : row) }))} /><span>{draft.unit}</span></label> : null}
              </div>
            ); })}
          </div>
        </div>
        <div className="flex justify-end gap-2"><button type="button" onClick={onClose} className="rounded-full border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-600">Cancel</button><button disabled={saving} className="rounded-full bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60">{saving ? 'Saving...' : item ? 'Save changes' : 'Add Inventory'}</button></div>
      </form>
    </ModalShell>
  );
}

export default function InventorySetup() {
  const location = useLocation();
  const [view, setView] = useState('current');
  const [selectedDomain, setSelectedDomain] = useState(domains[0]);
  const [items, setItems] = useState([]);
  const [auditInvoices, setAuditInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingRestock, setSavingRestock] = useState(false);
  const [savingAdjustment, setSavingAdjustment] = useState(false);
  const [restockItem, setRestockItem] = useState(null);
  const [adjustmentItem, setAdjustmentItem] = useState(null);
  const [ledgerItem, setLedgerItem] = useState(null);
  const [ledgerRows, setLedgerRows] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [bulkDialog, setBulkDialog] = useState(null);
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [inventoryModal, setInventoryModal] = useState({ open: false, item: null });
  const [inventoryOptions, setInventoryOptions] = useState({ domains, units: [], treatments: [] });
  const [savingInventory, setSavingInventory] = useState(false);
  const [inventorySearch, setInventorySearch] = useState('');
  const [inventoryCatalog, setInventoryCatalog] = useState([]);
  const [inventorySearchOpen, setInventorySearchOpen] = useState(false);
  const [pendingInventoryFocus, setPendingInventoryFocus] = useState(null);
  const [highlightedInventoryId, setHighlightedInventoryId] = useState(null);
  const inventoryRowRefs = useRef(new Map());
  const saveTimersRef = useRef(new Map());
  const pendingSavesRef = useRef(new Map());
  const saveVersionsRef = useRef(new Map());
  const confirmedValuesRef = useRef(new Map());
  const confirmedProvenanceRef = useRef(new Map());
  const confirmedVersionsRef = useRef(new Map());
  const [restockDraft, setRestockDraft] = useState({
    newAddedQty: '',
    ratePerUnit: '',
    supplierName: '',
    purchaseDate: new Date().toISOString().slice(0, 10),
    notes: '',
  });
  const [adjustmentDraft, setAdjustmentDraft] = useState({
    adjustmentQty: '',
    reason: '',
    notes: '',
  });
  const [pendingRestockTarget, setPendingRestockTarget] = useState({
    consumableId: null,
    domain: '',
  });
  const navigate = useNavigate();
  const financeStatus = useFinanceLockStatus();
  const user = useAuthStore((state) => state.user);
  const roleKey = normalizeRole(user?.role);
  const canEditInventory = [ROLE_KEYS.OWNER_ADMIN, ROLE_KEYS.DOCTOR].includes(roleKey);

  const openInventoryModal = async (item = null) => {
    try {
      const [optionsResponse, itemResponse] = await Promise.all([
        axios.get('/inventory/configuration-options'),
        item ? axios.get(`/inventory/items/${item.id}`) : Promise.resolve({ data: null }),
      ]);
      setInventoryOptions(optionsResponse.data);
      setInventoryModal({ open: true, item: itemResponse.data });
    } catch (error) { toast.error(getFriendlyInventoryError(error)); }
  };

  const saveInventoryItem = async (draft) => {
    setSavingInventory(true);
    try {
      const payload = { ...draft, mappings: draft.mappings.map(({ key, name, domain, isCustom, ...row }) => row) };
      if (inventoryModal.item) await axios.put(`/inventory/items/${inventoryModal.item.id}`, payload);
      else await axios.post('/inventory/items', payload);
      toast.success(inventoryModal.item ? 'Clinic inventory updated.' : 'Clinic inventory added.');
      setInventoryModal({ open: false, item: null });
      if (draft.domain !== selectedDomain) setSelectedDomain(draft.domain); else await fetchCurrentStock();
      const catalogResponse = await axios.get('/inventory/current');
      setInventoryCatalog(Array.isArray(catalogResponse.data) ? catalogResponse.data : []);
    } catch (error) { toast.error(getFriendlyInventoryError(error)); }
    finally { setSavingInventory(false); }
  };

  const normalizedInventorySearch = inventorySearch.trim().toLowerCase();
  const inventorySearchResults = normalizedInventorySearch
    ? inventoryCatalog.filter((item) =>
        `${item.name || ''} ${item.typeVariant || ''}`.toLowerCase().includes(normalizedInventorySearch)
      ).slice(0, 12)
    : [];
  const chooseInventorySearchResult = (item) => {
    setInventorySearch(item.name);
    setInventorySearchOpen(false);
    setPendingInventoryFocus({ id: item.id, domain: item.domain });
    if (selectedDomain !== item.domain) setSelectedDomain(item.domain);
  };
  const clearInventorySearch = () => {
    setInventorySearch('');
    setInventorySearchOpen(false);
    setPendingInventoryFocus(null);
    setHighlightedInventoryId(null);
  };

  useEffect(() => {
    const stateConsumableId = Number(location.state?.openRestockForConsumableId || 0);
    const stateDomain = String(location.state?.openRestockForDomain || '');
    const query = new URLSearchParams(location.search || '');
    const queryConsumableId = Number(query.get('restockId') || 0);
    const queryDomain = String(query.get('restockDomain') || '');
    const consumableId = stateConsumableId || queryConsumableId;
    const domain = stateDomain || queryDomain;

    if (consumableId > 0) {
      setPendingRestockTarget({ consumableId, domain });
    }
  }, [location.search, location.state]);

  useEffect(() => {
    if (view === 'current') {
      fetchCurrentStock();
    } else {
      fetchAuditList();
    }
  }, [view, selectedDomain]);

  useEffect(() => {
    let active = true;
    axios.get('/inventory/current').then((response) => {
      if (active) setInventoryCatalog(Array.isArray(response.data) ? response.data : []);
    }).catch(() => {
      if (active) setInventoryCatalog([]);
    });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!pendingInventoryFocus || loading || selectedDomain !== pendingInventoryFocus.domain) return undefined;
    const row = inventoryRowRefs.current.get(Number(pendingInventoryFocus.id));
    if (!row) return undefined;
    row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    row.focus({ preventScroll: true });
    setHighlightedInventoryId(Number(pendingInventoryFocus.id));
    setPendingInventoryFocus(null);
    const timer = window.setTimeout(() => setHighlightedInventoryId(null), 1800);
    return () => window.clearTimeout(timer);
  }, [items, loading, pendingInventoryFocus, selectedDomain]);

  useEffect(() => () => {
    saveTimersRef.current.forEach((timer) => clearTimeout(timer));
    saveTimersRef.current.clear();
  }, []);

  useEffect(() => {
    if (!pendingRestockTarget.consumableId) {
      return;
    }

    if (view !== 'current') {
      setView('current');
      return;
    }

    if (pendingRestockTarget.domain && selectedDomain !== pendingRestockTarget.domain) {
      setSelectedDomain(pendingRestockTarget.domain);
      return;
    }

    if (loading || !items.length) {
      return;
    }

    const matchedItem = items.find(
      (item) => Number(item.id) === Number(pendingRestockTarget.consumableId)
    );

    if (!matchedItem) {
      toast.error('Selected consumable not found in the current inventory domain.');
      setPendingRestockTarget({ consumableId: null, domain: '' });
      navigate('/inventory', { replace: true });
      return;
    }

    openRestockModal(matchedItem);
    setPendingRestockTarget({ consumableId: null, domain: '' });
    navigate('/inventory', { replace: true });
  }, [
    items,
    loading,
    navigate,
    pendingRestockTarget,
    selectedDomain,
    view,
  ]);

  const fetchCurrentStock = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/inventory/current?domain=${encodeURIComponent(selectedDomain)}`);
      const rows = Array.isArray(res.data) ? res.data : [];
      setItems(rows);
      rows.forEach((item) => {
        confirmedValuesRef.current.set(`${item.id}:currentStockQty`, item.currentStockQty);
        confirmedValuesRef.current.set(`${item.id}:ratePerUnit`, item.ratePerUnit);
        confirmedProvenanceRef.current.set(
          `${item.id}:currentStockQty`,
          item.provenance?.currentStockQty
        );
        confirmedProvenanceRef.current.set(
          `${item.id}:ratePerUnit`,
          item.provenance?.ratePerUnit
        );
      });
    } catch (err) {
      console.error(err);
      toast.error('Failed to load stock');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const commitStockField = async (key) => {
    const pending = pendingSavesRef.current.get(key);
    if (!pending) return;
    saveTimersRef.current.delete(key);

    try {
      const res = await axios.put(`/inventory/current/${pending.id}`, {
        [pending.field]: pending.value,
        reason: `Current Stock automatic ${pending.field} edit`,
      });
      const confirmedVersion = confirmedVersionsRef.current.get(key) || 0;
      if (pending.version > confirmedVersion) {
        confirmedValuesRef.current.set(key, res.data?.[pending.field] ?? pending.value);
        confirmedProvenanceRef.current.set(key, 'manual');
        confirmedVersionsRef.current.set(key, pending.version);
      }
      if (saveVersionsRef.current.get(key) !== pending.version) return;
      const nextItem = res.data || {};
      pendingSavesRef.current.delete(key);
      setItems((prev) =>
        prev.map((item) =>
          item.id === pending.id
            ? {
                ...item,
                ...nextItem,
                id: item.id,
                domain: item.domain,
                name: item.name,
                typeVariant: item.typeVariant,
                unit: item.unit,
              }
            : item
        )
      );
    } catch (err) {
      if (saveVersionsRef.current.get(key) !== pending.version) return;
      console.error(err);
      const confirmedValue = confirmedValuesRef.current.get(key);
      const confirmedProvenance = confirmedProvenanceRef.current.get(key);
      pendingSavesRef.current.delete(key);
      setItems((prev) => prev.map((item) => (
        item.id === pending.id
          ? {
              ...item,
              [pending.field]: confirmedValue,
              provenance: {
                ...(item.provenance || {}),
                [pending.field]: confirmedProvenance,
              },
            }
          : item
      )));
      toast.error(getFriendlyInventoryError(err));
    }
  };

  const updateStock = (id, field, value, flush = false) => {
    const key = `${id}:${field}`;
    const version = (saveVersionsRef.current.get(key) || 0) + 1;
    saveVersionsRef.current.set(key, version);
    pendingSavesRef.current.set(key, { id, field, value, version });
    setItems((prev) => prev.map((item) => (
      item.id === id
        ? {
            ...item,
            [field]: value,
            provenance: { ...(item.provenance || {}), [field]: 'manual' },
          }
        : item
    )));

    const existingTimer = saveTimersRef.current.get(key);
    if (existingTimer) clearTimeout(existingTimer);
    if (flush) {
      commitStockField(key);
    } else {
      saveTimersRef.current.set(key, setTimeout(() => commitStockField(key), 500));
    }
  };

  const flushStockField = (id, field) => {
    const key = `${id}:${field}`;
    const timer = saveTimersRef.current.get(key);
    if (timer) clearTimeout(timer);
    commitStockField(key);
  };

  const openEmptyFieldsDialog = () => {
    setBulkPreview(null);
    setBulkDialog('empty');
  };

  const openAutoPopulateDialog = async () => {
    setBulkDialog('auto');
    setBulkPreview(null);
    setBulkLoading(true);
    try {
      const response = await axios.post('/inventory/current/auto-populate', {
        mode: 'fill_missing_only',
        dryRun: true,
      });
      setBulkPreview(response.data);
    } catch (error) {
      toast.error(getFriendlyInventoryError(error));
      setBulkDialog(null);
    } finally {
      setBulkLoading(false);
    }
  };

  const submitEmptyFields = async () => {
    setBulkLoading(true);
    try {
      const response = await axios.post('/inventory/current/empty-fields', { confirmed: true });
      toast.success(response.data?.message || 'Inventory fields emptied.');
      setBulkDialog(null);
      await fetchCurrentStock();
    } catch (error) {
      toast.error(getFriendlyInventoryError(error));
    } finally {
      setBulkLoading(false);
    }
  };

  const submitAutoPopulate = async () => {
    if (!bulkPreview?.defaultsVersion) return;
    setBulkLoading(true);
    try {
      const response = await axios.post('/inventory/current/auto-populate', {
        mode: 'fill_missing_only',
        dryRun: false,
        confirmed: true,
        defaultsVersion: bulkPreview.defaultsVersion,
      });
      toast.success(response.data?.message || 'Starter inventory applied.');
      setBulkDialog(null);
      await fetchCurrentStock();
    } catch (error) {
      toast.error(getFriendlyInventoryError(error));
    } finally {
      setBulkLoading(false);
    }
  };

  const mergeUpdatedCurrentStock = (currentStock) => {
    if (!currentStock?.consumableId) {
      fetchCurrentStock();
      return;
    }

    setItems((prev) =>
      prev.map((item) =>
        Number(item.id) === Number(currentStock.consumableId)
          ? {
              ...item,
              currentStockId: currentStock.id || item.currentStockId,
              currentStockQty: currentStock.currentStockQty,
              balanceQty: currentStock.balanceQty ?? currentStock.currentStockQty,
              baselineStockQty: currentStock.baselineStockQty ?? item.baselineStockQty,
              ratePerUnit:
                currentStock.ratePerUnit === null || currentStock.ratePerUnit === undefined
                  ? item.ratePerUnit
                  : currentStock.ratePerUnit,
            }
          : item
      )
    );
  };

  const openRestockModal = (item) => {
    setRestockItem(item);
    setRestockDraft({
      newAddedQty: item.restockQty && Number(item.restockQty) > 0 ? String(item.restockQty) : '',
      ratePerUnit:
        item.ratePerUnit === null || item.ratePerUnit === undefined ? '' : String(item.ratePerUnit),
      supplierName: '',
      purchaseDate: new Date().toISOString().slice(0, 10),
      notes: '',
    });
  };

  const updateRestockDraft = (field, value) => {
    setRestockDraft((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const closeRestockModal = () => {
    setRestockItem(null);
  };

  const submitRestock = async (event) => {
    event.preventDefault();
    if (!restockItem) return;

    if (parseNumericValue(restockDraft.newAddedQty) <= 0) {
      toast.error('New Added Qty must be greater than zero.');
      return;
    }

    if (parseNumericValue(restockDraft.ratePerUnit) < 0 || restockDraft.ratePerUnit === '') {
      toast.error('Rate Per Unit is required.');
      return;
    }

    setSavingRestock(true);
    try {
      const res = await axios.post('/inventory/restock', {
        currentStockId: restockItem.currentStockId,
        consumableId: restockItem.id,
        newAddedQty: restockDraft.newAddedQty,
        ratePerUnit: restockDraft.ratePerUnit,
        supplierName: restockDraft.supplierName,
        purchaseDate: restockDraft.purchaseDate || null,
        notes: restockDraft.notes,
      });

      mergeUpdatedCurrentStock(res.data?.currentStock);
      toast.success('Stock restocked successfully.');
      closeRestockModal();
      if (ledgerItem && Number(ledgerItem.id) === Number(restockItem.id)) {
        await fetchLedger(restockItem);
      }
    } catch (err) {
      console.error(err);
      toast.error(getFriendlyInventoryError(err));
    } finally {
      setSavingRestock(false);
    }
  };

  const openAdjustmentModal = (item) => {
    setAdjustmentItem(item);
    setAdjustmentDraft({
      adjustmentQty: '',
      reason: '',
      notes: '',
    });
  };

  const updateAdjustmentDraft = (field, value) => {
    setAdjustmentDraft((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const closeAdjustmentModal = () => {
    setAdjustmentItem(null);
  };

  const submitAdjustment = async (event) => {
    event.preventDefault();
    if (!adjustmentItem) return;

    if (parseNumericValue(adjustmentDraft.adjustmentQty) === 0) {
      toast.error('Adjustment Qty cannot be zero.');
      return;
    }

    if (!String(adjustmentDraft.reason || '').trim()) {
      toast.error('Reason is required for manual adjustment.');
      return;
    }

    setSavingAdjustment(true);
    try {
      const res = await axios.post('/inventory/adjustment', {
        currentStockId: adjustmentItem.currentStockId,
        consumableId: adjustmentItem.id,
        adjustmentQty: adjustmentDraft.adjustmentQty,
        reason: adjustmentDraft.reason,
        notes: adjustmentDraft.notes,
      });

      mergeUpdatedCurrentStock(res.data?.currentStock);
      toast.success('Stock adjustment saved.');
      closeAdjustmentModal();
      if (ledgerItem && Number(ledgerItem.id) === Number(adjustmentItem.id)) {
        await fetchLedger(adjustmentItem);
      }
    } catch (err) {
      console.error(err);
      toast.error(getFriendlyInventoryError(err));
    } finally {
      setSavingAdjustment(false);
    }
  };

  const fetchLedger = async (item) => {
    if (!item?.id) return;

    setLedgerItem(item);
    setLedgerLoading(true);
    try {
      const res = await axios.get(`/inventory/ledger/${item.id}?limit=50`);
      setLedgerRows(Array.isArray(res.data?.rows) ? res.data.rows : []);
    } catch (err) {
      console.error(err);
      toast.error(getFriendlyInventoryError(err));
      setLedgerRows([]);
    } finally {
      setLedgerLoading(false);
    }
  };

  const closeLedgerModal = () => {
    setLedgerItem(null);
    setLedgerRows([]);
  };

  const fetchAuditList = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/inventory/audit');
      setAuditInvoices(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load audit list');
      setAuditInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const orderedItems = sortInventoryItemsByMasterSequence(items, selectedDomain);
  const hasStarterValues = items.some((item) =>
    item.provenance?.currentStockQty === 'starter' ||
    item.provenance?.ratePerUnit === 'starter'
  );
  const firstRestockCandidate =
    orderedItems.find((item) => parseNumericValue(item.restockQty) > 0) || orderedItems[0] || null;
  const renderFinanceCell = (children) => {
    if (financeStatus.loading) {
      return <span className="inline-block h-8 w-20 animate-pulse rounded-full bg-slate-200" />;
    }

    if (financeStatus.restricted || !financeStatus.canUnlock) {
      return <span className="text-sm font-semibold text-amber-600">Restricted</span>;
    }

    if (financeStatus.locked) {
      return <span className="text-sm font-semibold text-blue-600">Locked</span>;
    }

    return children;
  };

  return (
    <div className="space-y-3">
      <section className="rounded-[24px] border border-slate-200 bg-white px-5 py-4 shadow-[0_8px_22px_rgba(15,23,42,0.04)]">
        <div className="min-w-0">
          <div className="min-w-0">
            <h1 className="text-[28px] font-bold leading-tight tracking-tight text-slate-900">Inventory Setup</h1>
            <p className="mt-1.5 max-w-2xl text-sm leading-5 text-slate-500">Track stock, update quantities, and manage the materials used in your clinic.</p>
          </div>
        </div>
      </section>

      <div className="flex flex-col gap-2 rounded-[18px] border border-slate-200 bg-slate-50/80 p-1.5 shadow-[0_5px_16px_rgba(15,23,42,0.04)] sm:flex-row sm:items-center sm:justify-between">
        <div className="mobile-table-scroll">
          <div className="flex min-w-max gap-2">
            <button
              onClick={() => setView('current')}
              className={`${railButtonBase} ${view === 'current' ? railButtonActive : railButtonInactive}`}
            >
              Current Stock
            </button>
            <button
              onClick={() => setView('audit')}
              className={`${railButtonBase} ${view === 'audit' ? railButtonActive : railButtonInactive}`}
            >
              Audit
            </button>
          </div>
        </div>
        {canEditInventory ? <button type="button" onClick={() => openInventoryModal()} className="inline-flex w-full shrink-0 items-center justify-center rounded-full bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-[0_8px_18px_rgba(37,99,235,0.20)] transition hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-100 sm:w-auto"><PlusCircleIcon className="mr-1 h-5 w-5" />Add Inventory</button> : null}
      </div>

      {view === 'current' ? (
        <>
          <div className="hidden">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-4 top-1/2 z-10 h-5 w-5 -translate-y-1/2 text-blue-500" />
            <input type="search" value={inventorySearch} onFocus={() => setInventorySearchOpen(true)} onChange={(event) => { setInventorySearch(event.target.value); setInventorySearchOpen(true); }} onKeyDown={(event) => { if (event.key === 'Escape') setInventorySearchOpen(false); if (event.key === 'Enter' && inventorySearchResults[0]) { event.preventDefault(); chooseInventorySearchResult(inventorySearchResults[0]); } }} placeholder="Search inventory..." aria-label="Search inventory by name or variant" className="h-11 w-full rounded-2xl border border-blue-200 bg-blue-50/40 pl-12 pr-11 text-sm text-slate-800 outline-none transition placeholder:text-slate-500 focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100" />
            {inventorySearch ? <button type="button" onClick={clearInventorySearch} aria-label="Clear inventory search" className="absolute right-3 top-1/2 z-10 -translate-y-1/2 rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><XMarkIcon className="h-4 w-4" /></button> : null}
            {inventorySearchOpen && normalizedInventorySearch ? <div className="absolute z-40 mt-2 max-h-80 w-full overflow-y-auto rounded-2xl border border-slate-200 bg-white p-2 shadow-xl">{inventorySearchResults.length ? inventorySearchResults.map((item) => <button key={item.id} type="button" onClick={() => chooseInventorySearchResult(item)} className="flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-left hover:bg-blue-50"><span className="min-w-0"><span className="block truncate text-sm font-semibold text-slate-900">{item.name}</span><span className="block truncate text-xs text-slate-500">{item.typeVariant} · {item.domain}</span></span>{item.isCustom ? <span className="shrink-0 rounded-full bg-teal-50 px-2 py-1 text-[10px] font-semibold uppercase text-teal-700">Clinic Added</span> : null}</button>) : <p className="px-3 py-4 text-sm text-slate-500">No inventory found</p>}</div> : null}
          </div>
          <section className={`${shellClasses} overflow-hidden`}>
            <div className="flex flex-col gap-3 border-b border-slate-200 px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex shrink-0 items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                  <CubeIcon className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold tracking-tight text-slate-900">Current Stock</h2>
                </div>
              </div>

              <div className="relative w-full min-w-[240px] max-w-sm lg:flex-1">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-3.5 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-blue-500" />
                <input type="search" value={inventorySearch} onFocus={() => setInventorySearchOpen(true)} onChange={(event) => { setInventorySearch(event.target.value); setInventorySearchOpen(true); }} onKeyDown={(event) => { if (event.key === 'Escape') setInventorySearchOpen(false); if (event.key === 'Enter' && inventorySearchResults[0]) { event.preventDefault(); chooseInventorySearchResult(inventorySearchResults[0]); } }} placeholder="Search inventory..." aria-label="Search inventory by name or variant" className="h-10 w-full rounded-xl border border-blue-200 bg-blue-50/40 pl-10 pr-10 text-sm text-slate-800 outline-none transition placeholder:text-slate-500 focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100" />
                {inventorySearch ? <button type="button" onClick={clearInventorySearch} aria-label="Clear inventory search" className="absolute right-3 top-1/2 z-10 -translate-y-1/2 rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><XMarkIcon className="h-4 w-4" /></button> : null}
                {inventorySearchOpen && normalizedInventorySearch ? <div className="absolute z-40 mt-2 max-h-80 w-full overflow-y-auto rounded-2xl border border-slate-200 bg-white p-2 shadow-xl">{inventorySearchResults.length ? inventorySearchResults.map((item) => <button key={item.id} type="button" onClick={() => chooseInventorySearchResult(item)} className="flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-left hover:bg-blue-50"><span className="min-w-0"><span className="block truncate text-sm font-semibold text-slate-900">{item.name}</span><span className="block truncate text-xs text-slate-500">{item.typeVariant} · {item.domain}</span></span>{item.isCustom ? <span className="shrink-0 rounded-full bg-teal-50 px-2 py-1 text-[10px] font-semibold uppercase text-teal-700">Clinic Added</span> : null}</button>) : <p className="px-3 py-4 text-sm text-slate-500">No inventory found</p>}</div> : null}
              </div>

              <div className="flex flex-wrap gap-2">
                {canEditInventory ? (
                  <button
                    type="button"
                    onClick={() => firstRestockCandidate && openRestockModal(firstRestockCandidate)}
                    disabled={!firstRestockCandidate}
                    className="inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-white px-3.5 py-1.5 text-xs font-semibold text-blue-700 transition hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <PlusCircleIcon className="h-4 w-4" />
                    Restock Consumables
                  </button>
                ) : null}
                <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-600">
                  {selectedDomain}
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-500">
                  <ArchiveBoxIcon className="h-4 w-4 text-slate-400" />
                  {loading ? 'Refreshing...' : `${items.length} items`}
                </span>
              </div>
            </div>

            <div className="p-2.5">
              <div className="mobile-table-scroll rounded-[18px] border border-slate-200 bg-slate-50/80 p-1.5">
                <div className="flex min-w-max gap-2">
                  {domains.map((domain) => {
                    const isActive = selectedDomain === domain;

                    return (
                      <button
                        key={domain}
                        onClick={() => setSelectedDomain(domain)}
                        className={`${railButtonBase} ${
                          isActive ? railButtonActive : railButtonInactive
                        }`}
                      >
                        {domain}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>

          <section className={`${shellClasses} overflow-hidden`}>
            <div className="flex flex-col gap-2 border-b border-slate-200 px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h3 className="text-base font-semibold tracking-tight text-slate-900">Consumables</h3>
                <p className="mt-0.5 text-sm text-slate-500">
                  Enter your clinic&apos;s real stock and purchase rate.
                </p>
              </div>

              <div className="flex flex-col items-start gap-1.5 lg:min-w-[410px] lg:shrink-0 lg:items-end">
                <div className="flex flex-wrap gap-2 lg:justify-end">
                  {canEditInventory ? (
                    <button
                      type="button"
                      onClick={openEmptyFieldsDialog}
                      className="inline-flex items-center rounded-full border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-700 transition hover:bg-rose-100 focus:outline-none focus:ring-4 focus:ring-rose-100"
                    >
                      Empty Fields
                    </button>
                  ) : null}
                  {canEditInventory ? (
                    <button
                      type="button"
                      onClick={openAutoPopulateDialog}
                      className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 transition hover:bg-blue-100 focus:outline-none focus:ring-4 focus:ring-blue-100"
                    >
                      Auto Populate
                    </button>
                  ) : null}
                </div>
                {hasStarterValues ? (
                  <p className="text-[11px] font-medium leading-4 text-amber-700 lg:whitespace-nowrap lg:text-right" role="status">
                    Starter values are estimates — replace Qty &amp; Rate with actual stock.
                  </p>
                ) : null}
              </div>
            </div>

            <div className="mobile-table-scroll">
              <table className="mobile-card-table min-w-[1500px] divide-y divide-slate-200">
                <thead className="sticky top-0 z-20 bg-slate-50/95 shadow-[0_1px_0_rgba(148,163,184,0.35)]">
                  <tr>
                    <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                      Consumable Item Name
                    </th>
                    <th className={tableHeaderClasses}>Type / Variant</th>
                    <th className={tableHeaderClasses}>Current Stock Qty</th>
                    <th className={tableHeaderClasses}>Unit</th>
                    <th className={tableHeaderClasses}>{`Rate / Unit (${RUPEE_SYMBOL})`}</th>
                    <th className={tableHeaderClasses}>Amount</th>
                    <th className={tableHeaderClasses}>Balance Qty</th>
                    <th className={tableHeaderClasses}>Stock Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {loading ? (
                    <tr>
                      <td colSpan="8" data-full-row="true" className="px-6 py-8 text-center text-sm text-slate-500">
                        Loading inventory...
                      </td>
                    </tr>
                  ) : orderedItems.length > 0 ? (
                    orderedItems.map((item) => {
                      const balancePresentation = getBalanceTonePresentation(
                        item.stockHealthPercentage ?? item.restockQty
                      );

                      return (
                        <tr key={item.id} ref={(node) => { if (node) inventoryRowRefs.current.set(Number(item.id), node); else inventoryRowRefs.current.delete(Number(item.id)); }} tabIndex="-1" className={`outline-none transition duration-500 ${Number(highlightedInventoryId) === Number(item.id) ? 'bg-blue-50 ring-2 ring-inset ring-blue-200' : 'hover:bg-slate-50/50'}`}>
                          <td data-label="Consumable" className="px-5 py-4 text-left">
                            <div className="min-w-[220px]">
                              <p className="text-sm font-semibold text-slate-900">{item.name}</p>
                              {item.isCustom ? <span className="mt-1 inline-flex rounded-full bg-teal-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-teal-700">Clinic Added</span> : null}
                            </div>
                          </td>
                          <td data-label="Type / Variant" className={tableCellClasses}>{item.typeVariant}</td>
                          <td data-label="Current Stock" className={tableCellClasses}>
                            <QuantityInput
                              value={item.currentStockQty}
                              onChange={(e) => updateStock(item.id, 'currentStockQty', e.target.value)}
                              onBlur={() => flushStockField(item.id, 'currentStockQty')}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') e.currentTarget.blur();
                              }}
                              onCalculatedValue={(nextValue) =>
                                updateStock(item.id, 'currentStockQty', nextValue, true)
                              }
                              starter={item.provenance?.currentStockQty === 'starter'}
                              ariaLabel={`Current stock quantity for ${item.name}`}
                            />
                          </td>
                          <td data-label="Unit" className={`${tableCellClasses} font-medium text-slate-700`}>
                            {item.unit}
                          </td>
                          <td data-label="Rate / Unit" className={tableCellClasses}>
                            {renderFinanceCell(
                              <MoneyInput
                                  value={item.ratePerUnit}
                                  onChange={(e) => updateStock(item.id, 'ratePerUnit', e.target.value)}
                                  onBlur={() => flushStockField(item.id, 'ratePerUnit')}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') e.currentTarget.blur();
                                  }}
                                  starter={item.provenance?.ratePerUnit === 'starter'}
                                  ariaLabel={`Rate per unit for ${item.name}`}
                                />
                            )}
                          </td>
                          <td data-label="Amount" className={tableCellClasses}>
                            {renderFinanceCell(
                              <AmountInput
                                  value={getInventoryAmount(item)}
                                  quantity={item.currentStockQty}
                                  onCommit={(nextRatePerUnit) =>
                                    updateStock(item.id, 'ratePerUnit', nextRatePerUnit, true)
                                  }
                                  starter={
                                    item.provenance?.currentStockQty === 'starter' &&
                                    item.provenance?.ratePerUnit === 'starter'
                                  }
                                  ariaLabel={`Amount for ${item.name}`}
                                />
                            )}
                          </td>
                          <td data-label="Balance Qty" className={tableCellClasses}>
                            <span
                              className={balancePresentation.className}
                              data-restock-tone={balancePresentation.tone}
                            >
                              {parseNumericValue(item.balanceQty ?? item.currentStockQty).toFixed(2)}
                            </span>
                          </td>
                          <td data-label="Actions" className={tableCellClasses}>
                            <div className="mobile-card-actions flex flex-wrap justify-center gap-2">
                              {canEditInventory ? (
                                <button
                                  type="button"
                                  onClick={() => openRestockModal(item)}
                                  className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 transition hover:bg-blue-100"
                                >
                                  <PlusCircleIcon className="h-4 w-4" />
                                  Restock
                                </button>
                              ) : null}
                              {canEditInventory && item.isCustom ? <button type="button" onClick={() => openInventoryModal(item)} className="rounded-full border border-teal-200 bg-teal-50 px-3 py-1.5 text-xs font-semibold text-teal-700 hover:bg-teal-100">Edit</button> : null}
                              <button
                                type="button"
                                onClick={() => fetchLedger(item)}
                                className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-blue-200 hover:text-blue-700"
                              >
                                Ledger
                              </button>
                              {canEditInventory ? (
                                <button
                                  type="button"
                                  onClick={() => openAdjustmentModal(item)}
                                  className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700 transition hover:bg-amber-100"
                                >
                                  <AdjustmentsHorizontalIcon className="h-4 w-4" />
                                  Adjust
                                </button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="8" data-full-row="true" className="px-6 py-8 text-center text-sm text-slate-500">
                        No stock items found for this domain.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      ) : (
        <section className={`${shellClasses} overflow-hidden`}>
          <div className="flex flex-col gap-4 border-b border-slate-200 px-5 py-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-start gap-4">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
                <ClipboardDocumentListIcon className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-lg font-semibold tracking-tight text-slate-900">Audit Queue</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Review invoice-linked audit records and open the detailed audit screen when needed.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-500">
                <BanknotesIcon className="h-4 w-4 text-slate-400" />
                {loading ? 'Refreshing...' : `${auditInvoices.length} records`}
              </span>
            </div>
          </div>

          <div className="mobile-table-scroll">
            <table className="mobile-card-table min-w-[900px] divide-y divide-slate-200">
              <thead className="bg-slate-50/90">
                <tr>
                  <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                    Patient
                  </th>
                  <th className={tableHeaderClasses}>Phone</th>
                  <th className={tableHeaderClasses}>Date</th>
                  <th className={tableHeaderClasses}>Invoice #</th>
                  <th className={tableHeaderClasses}>Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {loading ? (
                  <tr>
                    <td colSpan="5" data-full-row="true" className="px-6 py-8 text-center text-sm text-slate-500">
                      Loading audit list...
                    </td>
                  </tr>
                ) : auditInvoices.length > 0 ? (
                  auditInvoices.map((inv) => (
                    <tr key={inv.id} className="transition hover:bg-slate-50/50">
                      <td data-label="Patient" className="px-5 py-4 text-left">
                        <span className="text-sm font-semibold text-slate-900">
                          {inv.Patient?.name || '-'}
                        </span>
                      </td>
                      <td data-label="Phone" className={tableCellClasses}>{inv.Patient?.phone || '-'}</td>
                      <td data-label="Date" className={tableCellClasses}>{inv.date || '-'}</td>
                      <td data-label="Invoice #" className={`${tableCellClasses} font-medium text-slate-700`}>
                        {inv.invoiceNumber || '-'}
                      </td>
                      <td data-label="Action" className={tableCellClasses}>
                        <button
                          onClick={() => navigate(`/inventory/audit/${inv.id}`)}
                          className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-3.5 py-1.5 text-sm font-semibold text-blue-700 transition hover:bg-blue-100"
                        >
                          Open Audit
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" data-full-row="true" className="px-6 py-8 text-center text-sm text-slate-500">
                      No audit records found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}
      <ClinicInventoryModal open={inventoryModal.open} item={inventoryModal.item} options={inventoryOptions} saving={savingInventory} onClose={() => setInventoryModal({ open: false, item: null })} onSave={saveInventoryItem} />

      {bulkDialog === 'empty' ? (
        <ModalShell title="Empty all inventory fields?" onClose={() => !bulkLoading && setBulkDialog(null)}>
          <div className="space-y-5 p-6">
            <p className="text-sm leading-6 text-slate-700">
              This will clear Qty and Rate/Unit for all 396 inventory items in this clinic. All inventory Amounts will become zero and pricing calculations may change. Inventory names, units, mappings and historical records will not be deleted.
            </p>
            <div className="flex flex-wrap justify-end gap-3">
              <button
                type="button"
                disabled={bulkLoading}
                onClick={() => setBulkDialog(null)}
                className="rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-600 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={bulkLoading}
                onClick={submitEmptyFields}
                className="rounded-full bg-rose-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-rose-700 disabled:opacity-60"
              >
                {bulkLoading ? 'Emptying...' : 'Empty All Fields'}
              </button>
            </div>
          </div>
        </ModalShell>
      ) : null}

      {bulkDialog === 'auto' ? (
        <ModalShell title="Use starter inventory estimates?" onClose={() => !bulkLoading && setBulkDialog(null)}>
          <div className="space-y-5 p-6">
            <p className="text-sm leading-6 text-slate-700">
              This fills unconfigured stock quantities and rates with temporary starter estimates so you can explore Dektal360Pro now. These are not your clinic&apos;s actual stock or supplier costs. Review and replace them for accurate inventory valuation, treatment costing and reports.
            </p>
            {bulkLoading && !bulkPreview ? (
              <p className="text-sm font-semibold text-slate-500">Preparing protected-value preview...</p>
            ) : bulkPreview ? (
              <div className="grid grid-cols-2 gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm">
                <span>Inventory rows</span><strong>{bulkPreview.totalRows}</strong>
                <span>Qty fields to fill</span><strong>{bulkPreview.quantityFieldsEligible}</strong>
                <span>Rate fields to fill</span><strong>{bulkPreview.rateFieldsEligible}</strong>
                <span>Existing fields protected</span><strong>{bulkPreview.existingFieldsProtected}</strong>
                <span>Catalogue coverage</span><strong>{bulkPreview.catalogueRowsMatched}/{bulkPreview.totalRows}</strong>
                <span>Version</span><strong>{bulkPreview.defaultsVersion}</strong>
                <span>Research date</span><strong>{bulkPreview.researchedOn}</strong>
              </div>
            ) : null}
            <p className="text-xs leading-5 text-amber-800">Zero values are treated as unconfigured for this confirmed action.</p>
            <div className="flex flex-wrap justify-end gap-3">
              <button
                type="button"
                disabled={bulkLoading}
                onClick={() => setBulkDialog(null)}
                className="rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-600 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={bulkLoading || !bulkPreview || bulkPreview.catalogueRowsMissing !== 0}
                onClick={submitAutoPopulate}
                className="rounded-full bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:opacity-60"
              >
                {bulkLoading ? 'Populating...' : 'Populate Inventory'}
              </button>
            </div>
          </div>
        </ModalShell>
      ) : null}

      {restockItem ? (
        <RestockModal
          draft={restockDraft}
          financeStatus={financeStatus}
          item={restockItem}
          onChange={updateRestockDraft}
          onClose={closeRestockModal}
          onSubmit={submitRestock}
          saving={savingRestock}
        />
      ) : null}

      {adjustmentItem ? (
        <AdjustmentModal
          draft={adjustmentDraft}
          item={adjustmentItem}
          onChange={updateAdjustmentDraft}
          onClose={closeAdjustmentModal}
          onSubmit={submitAdjustment}
          saving={savingAdjustment}
        />
      ) : null}

      {ledgerItem ? (
        <LedgerModal
          item={ledgerItem}
          ledger={ledgerRows}
          loading={ledgerLoading}
          onClose={closeLedgerModal}
        />
      ) : null}
    </div>
  );
}
